import React, { useState, useEffect } from 'react';
import { getData, putData } from '../helpers/ApiSettings';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getData('/users?sort[0]=username:asc');
      setUsers(response.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des utilisateurs');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const formatDate = (dateString) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateString));
  };

  const handleBlockUser = async (user) => {
    if (window.confirm(`Êtes-vous sûr de vouloir ${user.blocked ? 'débloquer' : 'bloquer'} cet utilisateur ?`)) {
      try {
        await putData(`/users/${user.id}`, {
          blocked: !user.blocked
        });
        await fetchUsers();
        alert(`Utilisateur ${user.blocked ? 'débloqué' : 'bloqué'} avec succès`);
      } catch (err) {
        alert(`Erreur lors de ${user.blocked ? 'déblocage' : 'blocage'} de l'utilisateur`);
        console.error('Error updating user:', err);
      }
    }
  };

  const handleToggleAdmin = async (user) => {
    const next = !user.is_admin;
    const verb = next ? 'donner les droits admin' : 'retirer les droits admin';
    if (window.confirm(`Êtes-vous sûr de vouloir ${verb} à ${user.username} ?\n\nUn utilisateur admin voit les tarifs vente privée comme un client référencé.`)) {
      try {
        await putData(`/users/${user.id}`, {
          is_admin: next
        });
        await fetchUsers();
        alert(`Droits admin ${next ? 'activés' : 'retirés'} avec succès`);
      } catch (err) {
        alert('Erreur lors de la modification des droits admin');
        console.error('Error updating admin status:', err);
      }
    }
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    const modal = new window.bootstrap.Modal(document.getElementById('userModal'));
    modal.show();
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Utilisateurs</h2>
        <button 
          onClick={fetchUsers} 
          className="btn btn-outline-primary"
        >
          Actualiser
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          {users.length === 0 ? (
            <p className="text-center text-muted my-5">Aucun utilisateur pour le moment</p>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nom d'utilisateur</th>
                    <th>Email</th>
                    <th>Date d'inscription</th>
                    <th>Statut</th>
                    <th>Rôle</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td>
                        <span className={`badge ${user.blocked ? 'bg-danger' : 'bg-success'}`}>
                          {user.blocked ? 'Bloqué' : 'Actif'}
                        </span>
                      </td>
                      <td>
                        {user.is_admin ? (
                          <span className="badge bg-warning text-dark">Admin</span>
                        ) : (
                          <span className="badge bg-light text-muted">Client</span>
                        )}
                      </td>
                      <td>
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-info me-2"
                            onClick={() => handleViewDetails(user)}
                          >
                            Détails
                          </button>
                          <button
                            className={`btn btn-sm me-2 ${user.is_admin ? 'btn-outline-warning' : 'btn-outline-secondary'}`}
                            onClick={() => handleToggleAdmin(user)}
                            title={user.is_admin ? 'Retirer les droits admin' : 'Donner les droits admin'}
                          >
                            {user.is_admin ? 'Retirer admin' : 'Rendre admin'}
                          </button>
                          <button
                            className={`btn btn-sm ${user.blocked ? 'btn-success' : 'btn-danger'}`}
                            onClick={() => handleBlockUser(user)}
                          >
                            {user.blocked ? 'Débloquer' : 'Bloquer'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal for user details */}
      <div className="modal fade" id="userModal" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Détails de l'utilisateur</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
            </div>
            {selectedUser && (
              <div className="modal-body">
                <dl className="row">
                  <dt className="col-sm-4">Nom d'utilisateur</dt>
                  <dd className="col-sm-8">{selectedUser.username}</dd>

                  <dt className="col-sm-4">Email</dt>
                  <dd className="col-sm-8">{selectedUser.email}</dd>

                  <dt className="col-sm-4">Date d'inscription</dt>
                  <dd className="col-sm-8">{formatDate(selectedUser.createdAt)}</dd>

                  <dt className="col-sm-4">Statut</dt>
                  <dd className="col-sm-8">
                    <span className={`badge ${selectedUser.blocked ? 'bg-danger' : 'bg-success'}`}>
                      {selectedUser.blocked ? 'Bloqué' : 'Actif'}
                    </span>
                  </dd>

                  <dt className="col-sm-4">Rôle</dt>
                  <dd className="col-sm-8">
                    {selectedUser.is_admin ? (
                      <span className="badge bg-warning text-dark">Admin</span>
                    ) : (
                      <span className="badge bg-light text-muted">Client</span>
                    )}
                  </dd>

                  {selectedUser.provider && (
                    <>
                      <dt className="col-sm-4">Méthode de connexion</dt>
                      <dd className="col-sm-8">{selectedUser.provider}</dd>
                    </>
                  )}
                </dl>

                {selectedUser.orders && selectedUser.orders.length > 0 && (
                  <div className="mt-4">
                    <h6>Dernières commandes</h6>
                    <ul className="list-unstyled">
                      {selectedUser.orders.slice(0, 5).map((order, index) => (
                        <li key={index} className="mb-2">
                          {formatDate(order.createdAt)} - {order.status} - {order.total}€
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                Fermer
              </button>
              {selectedUser && (
                <button
                  type="button"
                  className={`btn ${selectedUser.blocked ? 'btn-success' : 'btn-danger'}`}
                  onClick={() => handleBlockUser(selectedUser)}
                >
                  {selectedUser.blocked ? 'Débloquer' : 'Bloquer'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;